import type { PriceBreakdown, Property } from '../types';

/**
 * Format date to display format
 */
export const formatDate = (date: string | Date, locale = 'en-US'): string => {
  const d = new Date(date);
  return d.toLocaleDateString(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Format date to short format
 */
export const formatShortDate = (date: string | Date, locale = 'en-US'): string => {
  const d = new Date(date);
  return d.toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
  });
};

/**
 * Format date for API (ISO format)
 */
export const formatAPIDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Get day of week name
 */
export const getDayName = (date: string | Date, locale = 'en-US'): string => {
  const d = new Date(date);
  return d.toLocaleDateString(locale, { weekday: 'long' });
};

/**
 * Calculate nights between two dates
 */
export const calculateNights = (checkin: string, checkout: string): number => {
  const start = new Date(checkin);
  const end = new Date(checkout);
  const diffTime = end.getTime() - start.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Determine season based on date
 */
export const getSeason = (date: string | Date): 'low' | 'mid' | 'high' => {
  const d = new Date(date);
  const month = d.getMonth() + 1; // 1-12

  // High season: June 1 - September 30
  if (month >= 6 && month <= 9) {
    return 'high';
  }

  // Mid season: April, May, October
  if (month === 4 || month === 5 || month === 10) {
    return 'mid';
  }

  // Low season: November - March
  return 'low';
};

/**
 * Get base price for property based on season
 */
export const getBasePrice = (property: Property, season: 'low' | 'mid' | 'high'): number => {
  switch (season) {
    case 'low':
      return property.base_price_low;
    case 'mid':
      return property.base_price_mid;
    case 'high':
      return property.base_price_high;
    default:
      return property.base_price_mid;
  }
};

/**
 * Calculate total price with breakdown
 */
export const calculatePrice = (
  property: Property,
  checkin: string,
  checkout: string
): PriceBreakdown => {
  const nights = calculateNights(checkin, checkout);

  // Season based on check-in month
  const season = getSeason(checkin);
  const basePrice = getBasePrice(property, season);

  const total = basePrice * nights;

  return {
    basePrice,
    nights,
    seasonalAdjustment: 0,
    surgePrice: 0,
    cleaningFee: 0,
    serviceFee: 0,
    tax: 0,
    total,
  };
};

/**
 * Format price for display
 */
export const formatPrice = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

/**
 * Generate date range for calendar
 */
export const generateDateRange = (start: Date, end: Date): Date[] => {
  const dateArray: Date[] = [];
  const currentDate = new Date(start);
  
  while (currentDate <= end) {
    dateArray.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dateArray;
};

/**
 * Check if date is in unavailable dates list
 */
export const isDateUnavailable = (date: string | Date, unavailableDates: string[]): boolean => {
  const d = new Date(date);
  const isoDate = d.toISOString().split('T')[0];
  return unavailableDates.includes(isoDate);
};

/**
 * Get month name and year for calendar header
 */
export const getMonthYear = (date: string | Date, locale = 'en-US'): string => {
  const d = new Date(date);
  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
};

/**
 * Check if two date ranges overlap
 */
export const doDateRangesOverlap = (
  start1: string,
  end1: string,
  start2: string,
  end2: string
): boolean => {
  const s1 = new Date(start1);
  const e1 = new Date(end1);
  const s2 = new Date(start2);
  const e2 = new Date(end2);

  // Adjust for checkout being exclusive
  e1.setDate(e1.getDate() - 1);
  e2.setDate(e2.getDate() - 1);

  return s1 <= e2 && s2 <= e1;
};
